import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GridColumn, RowAction } from '../../../types/datagrid';
import { GridCell } from './GridCell';
import { MoreVertical, Eye, Pencil, Trash2, Copy, Archive } from 'lucide-react';

interface GridRowProps {
  row: any;
  columns: GridColumn[];
  index: number;
  rowId: string;
  isSelected?: boolean;
  isEditing?: boolean;
  editingCell?: {
    rowId: string;
    columnId: string;
    value: any;
  };
  height?: number;
  compactDensity?: boolean;
  onSelect?: (selected: boolean) => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onCellEdit?: (rowId: string, columnId: string, value: any) => Promise<void>;
  permissions?: {
    canEdit?: boolean;
    canDelete?: boolean;
  };
  rowActions?: RowAction[];
  onRowAction?: (rowId: string, action: string, rowData: any) => void;
}

export const GridRow: React.FC<GridRowProps> = ({
  row,
  columns,
  index,
  rowId,
  isSelected = false,
  isEditing = false,
  editingCell,
  height = 44,
  compactDensity = true,
  onSelect,
  onClick,
  onDoubleClick,
  onCellEdit,
  permissions = {},
  rowActions = [],
  onRowAction,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const actionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate menu position
  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 160; // min-w-[160px]
    const menuHeight = 200; // Approximate max height
    const padding = 8;

    let top = rect.bottom + 4;
    let left = rect.right - menuWidth;

    // Adjust if menu would go off right edge
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - menuWidth - padding;
    }

    // Adjust if menu would go off left edge
    if (left < padding) {
      left = padding;
    }

    // Adjust if menu would go off bottom edge
    if (top + menuHeight > window.innerHeight - padding) {
      top = rect.top - menuHeight - 4;
      if (top < padding) {
        top = padding;
      }
    }

    setMenuPosition({ top, left });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsRef.current &&
        !actionsRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsActionsOpen(false);
      }
    };

    const handlePositionUpdate = () => {
      if (isActionsOpen) {
        updateMenuPosition();
      }
    };

    if (isActionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handlePositionUpdate, true);
      window.addEventListener('resize', handlePositionUpdate);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handlePositionUpdate, true);
        window.removeEventListener('resize', handlePositionUpdate);
      };
    }
  }, [isActionsOpen, updateMenuPosition]);

  // Get icon for action
  const getActionIcon = (actionId: string) => {
    switch (actionId) {
      case 'view':
        return <Eye className="h-4 w-4" />;
      case 'edit':
        return <Pencil className="h-4 w-4" />;
      case 'delete':
        return <Trash2 className="h-4 w-4" />;
      case 'duplicate':
        return <Copy className="h-4 w-4" />;
      case 'archive':
        return <Archive className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const handleActionClick = (e: React.MouseEvent, actionId: string) => {
    e.stopPropagation();
    setIsActionsOpen(false);
    onRowAction?.(rowId, actionId, row);
  };

  const handleRowClick = (event: React.MouseEvent) => {
    // Don't trigger row click if clicking on interactive elements
    const target = event.target as HTMLElement;
    const inputTarget = event.target as HTMLInputElement;
    if (
      inputTarget.type === 'checkbox' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT'
    ) {
      return;
    }

    onClick?.();
  };

  const handleRowDoubleClick = (event: React.MouseEvent) => {
    // Don't trigger row double-click if clicking on interactive elements
    const target = event.target as HTMLElement;
    const inputTarget = event.target as HTMLInputElement;
    if (
      inputTarget.type === 'checkbox' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT'
    ) {
      return;
    }

    onDoubleClick?.();
  };

  const handleCellEdit = (columnId: string, value: any) => {
    return onCellEdit?.(rowId, columnId, value) || Promise.resolve();
  };

  return (
    <div
      className={`
        flex items-center group transition-colors duration-150
        ${isSelected ? 'bg-brand-50 border-l-4 border-l-brand-500' : 'border-l-4 border-l-transparent'}
        ${isHovered ? 'bg-gray-50' : ''}
        ${isEditing ? 'bg-blue-50 border-l-blue-500' : ''}
        ${onClick ? 'cursor-pointer' : ''}
      `}
      style={{ height }}
      role="row"
      aria-rowindex={index + 1}
      aria-selected={isSelected}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleRowClick}
      onDoubleClick={handleRowDoubleClick}
    >
      {/* Row Selection */}
      {onSelect && (
        <div className="px-3 flex items-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={e => onSelect(e.target.checked)}
            className="h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
            aria-label={`Select row ${index + 1}`}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Data Cells */}
      <div className="flex flex-1">
        {columns.map((column, columnIndex) => {
          const cellValue = row[column.key];
          const isCellEditing = editingCell?.rowId === rowId && editingCell?.columnId === column.id;

          return (
            <GridCell
              key={column.id}
              value={cellValue}
              column={column}
              columnIndex={columnIndex}
              rowData={row}
              rowId={rowId}
              isEditing={isCellEditing}
              editingValue={isCellEditing ? editingCell?.value : undefined}
              compactDensity={compactDensity}
              onEdit={column.isEditable && permissions.canEdit ? handleCellEdit : undefined}
              permissions={permissions}
            />
          );
        })}
      </div>

      {/* Row Actions */}
      {rowActions.length > 0 && (
        <div ref={actionsRef} className="relative flex items-center px-2" style={{ width: 48 }}>
          <button
            ref={buttonRef}
            type="button"
            onClick={e => {
              e.stopPropagation();
              if (!isActionsOpen) {
                updateMenuPosition();
              }
              setIsActionsOpen(!isActionsOpen);
            }}
            className={`
              p-1.5 rounded-md transition-colors
              ${isActionsOpen ? 'bg-gray-200 text-gray-900' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}
              ${isHovered || isActionsOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            `}
            aria-label="Row actions"
            aria-expanded={isActionsOpen}
            aria-haspopup="menu"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {/* Actions Dropdown - Rendered via Portal */}
          {isActionsOpen &&
            createPortal(
              <>
                {/* Invisible backdrop for click outside */}
                <div
                  className="fixed inset-0 z-[9998]"
                  onClick={() => setIsActionsOpen(false)}
                  aria-hidden="true"
                />
                <div
                  ref={dropdownRef}
                  className="fixed z-[9999] min-w-[160px] bg-white rounded-lg shadow-lg border border-gray-200 py-1 animate-in fade-in slide-in-from-top-1 duration-150"
                  style={{
                    top: menuPosition.top,
                    left: menuPosition.left,
                  }}
                  role="menu"
                >
                  {rowActions.map(action => {
                    const isVisible = action.isVisible ? action.isVisible(row) : true;
                    const isDisabled = action.isDisabled ? action.isDisabled(row) : false;

                    if (!isVisible) return null;

                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={e => handleActionClick(e, action.id)}
                        disabled={isDisabled}
                        className={`
                          w-full flex items-center space-x-2 px-3 py-2 text-sm text-left transition-colors
                          ${
                            action.variant === 'destructive'
                              ? 'text-red-600 hover:bg-red-50'
                              : 'text-gray-700 hover:bg-gray-100'
                          }
                          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        role="menuitem"
                      >
                        <span className="flex-shrink-0">{getActionIcon(action.id)}</span>
                        <span>{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>,
              document.body
            )}
        </div>
      )}
    </div>
  );
};
